
import { TAG_USER_TYPES, TAG_USER_ROLES, TAG_TYPES, TAG_DEFAULT_STYLE } from '../../data/tags';
import { strings } from '../../localization';

export const TAG_ACTIONS = {
    ADD: "addTag",
    EDIT: "edit",
    DELETE: "delete",
    ASSIGN_USER: "assignUser",
    CHANGE_USER_ROLE: "changeUserRole",
    UNASSIGN_USER: "unassignUser",
    ASSIGN_RAVEN: "assignRaven",
    UNASSIGN_RAVEN: "unassignRaven",
}

export default class TagsUtil {
    static convertToHierarchy = (tags) => {
        if(!tags) return [];
        const tagMap = {};
        const result = [];
    
        // create a map for easy access to each tag by its id
        tags.forEach(tag => {
            tagMap[tag.tagId] = { ...tag, isLeaf: true, isRoot: false };
        });
    
        // build the tree structure
        tags.forEach(tag => {
            if (tag.parentTagId) {
    
                let parent = tagMap[tag.parentTagId];
    
                if (parent) {
                    if(!parent.childTags) {
                        parent.childTags = [];
                    }

                    parent.childTags.push(tagMap[tag.tagId]);
                    parent.isLeaf = false;
                }
                else {
                    result.push(tagMap[tag.tagId]);
                }
            }
            else {
                tagMap[tag.tagId].isRoot = true;
                result.push(tagMap[tag.tagId]);
            }
        });
    
        
        return result;
    }

    static getCopyOfTag = (tag, ...additionalProps) => {

        const mergedProps = additionalProps.reduce((acc, prop) => {
            return { ...acc, ...prop };
        }, {});

        return {
            tagId: tag.tagId,
            name: tag.name,
            style: {color: tag.style.color, fontColor: tag.style.fontColor},
            parentTagId: tag.parentTagId,
            type: tag.type,
            isRootTag: tag.isRootTag,
            ancestors: tag.ancestors,
            canEdit: tag.canEdit,
            ...mergedProps
        };
    }

    static includeTagAncestorsInList = (tag, list, tags) => {
        let currentTag = tag;

        while(currentTag.parentTagId){
            
            let parentTag = list.find(t => t.tagId === currentTag.parentTagId);

            if(parentTag){
                currentTag = parentTag;
            }else{
                parentTag = tags.find(t => t.tagId === currentTag.parentTagId);
                if(parentTag){
                    list.push(parentTag);
                    currentTag = parentTag;
                }
                else{
                    break;
                }
            }

        }

        return list;
    }

    static getUuidOfRavensInTheSelectedTags = (tags) => {
        const ravensUuids = [];

        tags.forEach(tag => {
            tag.ravens.forEach(raven => {
                if(!ravensUuids.find(uuid => uuid === raven.ravenUuid)) {
                    ravensUuids.push(raven.ravenUuid);
                }
            });
        });

        return ravensUuids;
    }

    static getFontColor = (tagColor) => {
        const [r, g, b] = hexToRgb(tagColor);
        return isDark(r, g, b) ? "#FFFFFF" : "#000000";
    }

    static getHighestRole = (...roles) => {

        if(roles.includes(TAG_USER_ROLES.ADMIN)){
            return TAG_USER_ROLES.ADMIN;
        }
        else if(roles.includes(TAG_USER_ROLES.GUEST)){
            return TAG_USER_ROLES.GUEST;
        }
    
        return null;
    }

    static changeTagParent = (tag, newParentTag) => {

        tag.parentTagId = newParentTag.tagId;
        tag.ancestors = newParentTag.ancestors;
        tag.type = newParentTag.type !== TAG_TYPES.SYSTEM ? newParentTag.type : TAG_TYPES.EQUIPMENT;
        tag.style = newParentTag.type !== TAG_TYPES.SYSTEM ? newParentTag.style : TAG_DEFAULT_STYLE.EQUIPMENT;

        // exclude any selected ravens that are not on the new parent tag
        tag.ravens = tag.ravens.filter(tagRaven => newParentTag.ravens.find(parentTagRaven => parentTagRaven.ravenUuid === tagRaven.ravenUuid));

        // users that are exclusively inherited from previous parent tag, should be removed
        tag.users = tag.users.filter(tagUser => {
            if(tagUser.type === TAG_USER_TYPES.INHERITED_USER){
                return false;
            }

            return true;
        })

        // Add new parent users to the list
        newParentTag.users.forEach(parentTagUser => {
            const tagUser = tag.users.find(tagUser => tagUser.email === parentTagUser.email);

            if(!tagUser){
                tag.users.push({
                    ...parentTagUser, 
                    type: TAG_USER_TYPES.INHERITED_USER, 
                    inheritedRole: this.getHighestRole(parentTagUser.inheritedRole, parentTagUser.role)
                });
            }
            else {
                tagUser.type = TAG_USER_TYPES.INHERITED_USER;
                tagUser.role = this.getHighestRole(tagUser.role, parentTagUser.role);
                tagUser.inheritedRole = this.getHighestRole(parentTagUser.inheritedRole, parentTagUser.role);
            }
        });
    }

    static getErrorMessage = (error, action, ...params) => {
        const errors = [];

        if(error){
            if(error.errors){
                error.errors.forEach(err => {
                    if(err.message){
                        errors.push(err.message);
                    }
                });
            }
            else{
                if(error.message){
                    errors.push(error.message);
                }
            }
        }

        if(errors.length === 0) {
            const msg = getGenericErrorMessage(action);
            return strings.formatString(msg, ...params);
        }

        switch (true) {
            case errors.includes("Unable to create tag (already exists)"):
                return strings.tags_save_new_error_already_exists;
            case errors.includes("New tag name already exists"):
                return strings.tags_save_edits_name_error_already_exists;
            case errors.includes("exception access tag"):
            case errors.includes("You do not have access to this Raven"):
                return strings.tags_error_access;
            default:
                const msg = getGenericErrorMessage(action);
                return strings.formatString(msg, ...params);
        }
        
    }
}

// Utility function to calculate luminance based on RGB values
const calculateLuminance = (r, g, b) => {
    const [R, G, B] = [r, g, b].map((value) => {
        const normalizedValue = value / 255;
        return normalizedValue <= 0.03928 ? normalizedValue / 12.92 : Math.pow((normalizedValue + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

// Function to determine if a color is light or dark
const isDark = (r, g, b) => calculateLuminance(r, g, b) < 0.5;

const hexToRgb = (hex) => {
    // Remove the "#" and ensure it's a valid hex
    if (hex.startsWith('#')) hex = hex.slice(1);

    // Parse the hex string to RGB values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return [r, g, b];
};

const getGenericErrorMessage = (action) => {
    switch (action) {
        case TAG_ACTIONS.ADD:
            return strings.tags_save_new_error;
        case TAG_ACTIONS.EDIT:
            return strings.tags_save_edits_error;
        case TAG_ACTIONS.DELETE:
            return strings.tags_delete_tag_error;
        case TAG_ACTIONS.ASSIGN_USER:
            return strings.tags_assign_error;
        case TAG_ACTIONS.CHANGE_USER_ROLE:
            return strings.tags_user_role_update_error;
        case TAG_ACTIONS.UNASSIGN_USER:
            return strings.tags_unassign_error;
        case TAG_ACTIONS.ASSIGN_RAVEN:
            return strings.tags_assign_error;
        case TAG_ACTIONS.UNASSIGN_RAVEN:
            return strings.tags_unassign_error;
        default:
            return strings.app_an_error_occurred;
    }
}